import { Formik, Form } from 'formik'
import { toInteger } from 'lodash'
import { useQueryClient } from '@tanstack/react-query'
import moment from 'moment'
import { assoc, evolve, path, pick, pipe } from 'ramda'
import { useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import * as Yup from 'yup'
import { Button } from '@/components/button'
import { DefaultButtonBar } from '@/components/button-bar'
import { leftCloud } from '@/components/clouds'
import { Row, Group, FieldDropdown, FieldDatePicker, FieldInput } from '@/components/form'
import DashboardHeader from '@/features/Listing/dashboard-header'
import useMediaQuery from '@/hooks/use-media-query'
import { colors, breakpoints, fontWeights } from '@/styles'
import { defaultIfFalsy } from '@/utils'
import { stringToNumber } from '@/utils/string-to-numerical'
import { ErrorText } from '@/components/form/error'
import TenantSelector from './tenant-selector'
import backgroundTopRight from './images/background-top-right.png'
import backgroundTopMobile from './images/background-top-mobile.png'
import backgroundBottom from './images/background-bottom.png'

const Wrapper = styled.div`
  padding: 30px 100px 62px;
  min-height: calc(100vh - 190px);
  background: ${colors.white};
  display: flex;
  justify-content: flex-start;

  background-image: url(${leftCloud}), url(${backgroundTopRight}), url(${backgroundBottom});
  background-repeat: no-repeat;
  background-position: -90px 0px, 100% 0px, 100% calc(100% - 30px);
  background-size: 238px, 200px, 270px;

  @media screen and (max-width: ${breakpoints.desktop}) {
    padding: 30px 50px 140px;
    background-image: url(${leftCloud}), url(${backgroundTopMobile}), url(${backgroundBottom});
    background-position: -90px 0px, 100% 0px, 100% calc(100% - 20px);
    background-size: 238px, 80px, 200px;
  }

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    padding: 10px 25px 140px;
    min-height: calc(100vh - 174px);
  }

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    padding: 10px 25px 165px;
    min-height: calc(100vh - 45px);
    background-image: url(${backgroundTopMobile}), url(${backgroundBottom});
    background-position: 100% 0px, 110% calc(100% - 70px);
    background-size: 80px, 170px;
  }
`

const ContentWrapper = styled.div`
  max-width: 685px;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
`

const SectionHeading = styled.h1`
  font-size: 25px;
  color: ${colors.regular};
  font-weight: ${fontWeights.bold};
  line-height: 34px;
  margin: 0 0 30px;
`

const Subtitle = styled.h3`
  color: ${colors.regular};
  font-size: 18px;
  line-height: 24px;
  font-weight: ${fontWeights.medium};
  margin-top: ${props => props.top || 0}px;
  margin-bottom: 25px;
`

const Break = styled.div`
  ${props =>
    props.full &&
    `
    flex-basis: 100%;
  `}
  height: 0;
`

const formatRentalTerms = values => {
  const tenantIds = [{ user_id: toInteger(values.tenant.id) }]
  return pipe(
    assoc('contract_rentals', tenantIds),
    evolve({
      lease_type: defaultIfFalsy(undefined),
      start_date: defaultIfFalsy(null),
      end_date: defaultIfFalsy(null),
      lease_end_action: defaultIfFalsy(null),
      price: stringToNumber(undefined),
      price_frequency: defaultIfFalsy(undefined)
    })
  )(
    pick(
      ['lease_type', 'start_date', 'end_date', 'lease_end_action', 'price', 'price_frequency'],
      values
    )
  )
}

const MarkRented = ({
  history,
  match: {
    params: { listingId }
  }
}) => {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const displayMobile = useMediaQuery(`(max-width: ${breakpoints.phoneMax})`)
  const colSize = displayMobile ? 12 : 6
  const listingData = useSelector(path(['listing', listingId]))

  useEffect(() => {
    dispatch.listing.load(listingId)
  }, [dispatch.listing, listingId])

  useEffect(() => {
    // Invalidate listing data when state is changed
    const invalidate = () =>
      queryClient.invalidateQueries({ queryKey: ['listing-info', listingId] })
    // Redirect if listing is already rented or is not published
    const stateMachine = listingData?.listings?.state_machine
    const rentedStates = ['history', 'upcoming', 'rented']
    const incompleteStates = ['draft', 'pending']
    if (rentedStates.includes(stateMachine)) {
      invalidate()
      history.push(`/tenant/${listingId}`)
    } else if (incompleteStates.includes(stateMachine)) {
      invalidate()
      history.push(`/listings`)
    }
  }, [listingData, listingId, history, queryClient])

  const formOptions = () => ({
    initialValues: {
      tenant: {},
      lease_type: '',
      start_date: '',
      end_date: '',
      lease_end_action: '',
      price: '',
      price_frequency: ''
    },
    validationSchema: Yup.object().shape({
      tenant: Yup.object().shape({
        id: Yup.string().required()
      }),
      lease_type: Yup.string().required(),
      start_date: Yup.date().required(
        t('l.contract.create.start_date_required.error', 'Start date is a required field')
      ),
      end_date: Yup.mixed().when('lease_type', {
        is: 'fixed',
        then: Yup.date()
          .required(t('l.contract.create.end_date_required.error', 'End date is a required field'))
          .min(
            Yup.ref('start_date'),
            t(
              'l.contract.create.end_date_minimum.error',
              'End date must be greater than the start date'
            )
          ),
        otherwise: Yup.string()
      }),
      lease_end_action: Yup.mixed().when('lease_type', {
        is: 'fixed',
        then: Yup.string().required(
          t('l.contract.create.lease_end_action_required.error', 'Lease end action is required')
        ),
        otherwise: Yup.string()
      }),
      price: Yup.number()
        .typeError(t('l.contract.create.rental_price_type.error', 'Rental price must be a number'))
        .positive()
        .integer(
          t('l.contract.create.rental_price_integer.error', 'Rental price must be a whole number')
        )
        .required(),
      price_frequency: Yup.string().required()
    }),
    onSubmit: async (values, { setSubmitting }) => {
      const body = formatRentalTerms(values)
      const { response } = await dispatch.listing.markAsRentedWithInquiries({
        body,
        listingId
      })
      if (response.ok) {
        history.push(`/tenant/${listingId}`)
      }
      setSubmitting(false)
    }
  })

  return (
    <>
      <Helmet>
        <title>{t('l.listing.edit.mark_as_rented.title', 'Mark as Rented')}</title>
      </Helmet>
      <DashboardHeader
        listingId={listingId}
        currentPage={t('l.listing.edit.mark_as_rented.title', 'Mark as Rented')}
        height={55}
      />
      <Formik {...formOptions()}>
        {({ values, setFieldValue, isSubmitting, errors }) => (
          <Form style={{ flex: 1 }}>
            <Wrapper>
              <ContentWrapper>
                <SectionHeading>
                  {t('l.listing.edit.mark_as_rented.title', 'Mark as Rented')}
                </SectionHeading>
                <Subtitle>{t('l.contract.edit.the_tenant.label', 'The Tenant')}</Subtitle>
                <TenantSelector
                  values={values}
                  setFieldValue={setFieldValue}
                  listingId={listingId}
                />
                {errors?.tenant?.id && (
                  <ErrorText role="alert">
                    {t('b.mark_rented.edit.at_least_one.error', 'Please add a tenant')}
                  </ErrorText>
                )}
                <Subtitle top={45}>
                  {t('l.contract.edit.tenancy_details.label', 'Tenancy Details')}
                </Subtitle>
                <Row>
                  <Group col={colSize}>
                    <FieldDropdown
                      name="lease_type"
                      placeholder={t('l.contract.edit.lease_type.label', 'Lease type')}
                      required
                      options={[
                        {
                          value: 'fixed',
                          label: t('common.fixed_term', 'Fixed term')
                        },
                        {
                          value: 'month_to_month',
                          label: t('common.month_to_month', 'Month to month')
                        }
                      ]}
                    />
                  </Group>
                  <Break full={displayMobile} />
                  <Group col={colSize}>
                    <FieldDatePicker
                      name="start_date"
                      placeholder={t('l.contract.edit.start_date.label', 'Start date')}
                      required
                      maxDateMessage={null}
                    />
                  </Group>
                </Row>
                {values.lease_type === 'fixed' && (
                  <Row>
                    <Group col={colSize}>
                      <FieldDatePicker
                        name="end_date"
                        disabled={!values.start_date}
                        placeholder={t('l.contract.edit.end_date.label', 'End date')}
                        required
                        minDate={moment(values.start_date)}
                        minDateMessage={null}
                      />
                    </Group>
                    <Group col={colSize}>
                      <FieldDropdown
                        name="lease_end_action"
                        placeholder={t(
                          'l.contract.edit.when_lease_expires.label',
                          'When Lease Expires'
                        )}
                        required
                        options={[
                          {
                            value: 'continue_month_to_month',
                            label: t(
                              'l.contract.edit.continue_monthly.label',
                              'Continue month to month'
                            )
                          },
                          {
                            value: 'tenant_must_vacate',
                            label: t('l.contract.edit.must_move_out.label', 'Tenant must move out')
                          }
                        ]}
                      />
                    </Group>
                  </Row>
                )}
                <Subtitle top={30}>
                  {t('l.contract.edit.rent_details.label', 'Rent Details')}
                </Subtitle>
                <Row>
                  <Group col={colSize}>
                    <FieldInput
                      name="price"
                      placeholder={t('l.contract.edit.rental_price.label', 'Rent price')}
                      required
                      prefix="$"
                    ></FieldInput>
                  </Group>
                  <Group col={colSize}>
                    <FieldDropdown
                      name="price_frequency"
                      placeholder={t('l.contract.edit.payment_freq.label', 'Payment frequency')}
                      required
                      options={[
                        {
                          value: 'daily',
                          label: t('common.frequency_daily', 'Daily')
                        },
                        {
                          value: 'weekly',
                          label: t('common.frequency_weekly', 'Weekly')
                        },
                        {
                          value: 'monthly',
                          label: t('common.frequency_monthly', 'Monthly')
                        }
                      ]}
                    />
                  </Group>
                </Row>
              </ContentWrapper>
            </Wrapper>
            <DefaultButtonBar>
              <Button theme="primary" size="small" type="submit" disabled={isSubmitting}>
                {t('common.done', 'Done')}
              </Button>
            </DefaultButtonBar>
          </Form>
        )}
      </Formik>
    </>
  )
}

export default MarkRented
